import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";

const NAME_LABELS = { coach: "Coach Name", club: "Club Name", host: "Host Name" };

const CLUB_SUB_TYPE_LABELS = { running_club: "Running Club", cycling_club: "Cycling Club" };

const blankCoach = {
  type: "coach",
  clubSubType: "",
  coachName: "",
  title: "",
  location: "",
  description: "",
  profileimg: null,
  coverimage: null,
  website: "",
  facebook: "",
  instagram: "",
  tiktok: "",
  achievements: "",
  credentials: [""],
  amenities: [{ title: "", description: "" }],
  headUserId: null,
  capacity: "",
};

const CoachForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const googleKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [coach, setCoach] = useState(blankCoach);
  const [profilePreview, setProfilePreview] = useState("");
  const [coverPreview, setCoverPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [headUserOption, setHeadUserOption] = useState(null);
  const [headUserOptions, setHeadUserOptions] = useState([]);
  const [headUserSearchInput, setHeadUserSearchInput] = useState("");
  const [headUserLoading, setHeadUserLoading] = useState(false);
  const [placeOptions, setPlaceOptions] = useState([]);
  const [placesReady, setPlacesReady] = useState(false);

  const autoServiceRef = useRef(null);

  const nameLabel = NAME_LABELS[coach.type] || "Name";

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    let url;
    if (coach.profileimg instanceof File) {
      url = URL.createObjectURL(coach.profileimg);
      setProfilePreview(url);
    } else {
      setProfilePreview(coach.profileimg || coach.coachImage || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [coach.profileimg, coach.coachImage]);

  useEffect(() => {
    let url;
    if (coach.coverimage instanceof File) {
      url = URL.createObjectURL(coach.coverimage);
      setCoverPreview(url);
    } else {
      setCoverPreview(coach.coverimage || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [coach.coverimage]);

  useEffect(() => {
    if (!googleKey) {
      setPlacesReady(false);
      return;
    }

    const initGoogle = () => {
      if (!window.google?.maps?.places) return;
      autoServiceRef.current = new window.google.maps.places.AutocompleteService();
      setPlacesReady(true);
    };

    if (window.google?.maps?.places) {
      initGoogle();
      return;
    }

    const scriptId = "google-places-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      script.onerror = () => toast.error("Google Places failed to load. Please check the API key and allowed domains.");
      document.body.appendChild(script);
    } else {
      const waitForGoogle = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(waitForGoogle);
          initGoogle();
        }
      }, 250);
      return () => clearInterval(waitForGoogle);
    }
  }, [googleKey]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}coach/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        setCoach({
          type: data.type || "coach",
          clubSubType: data.clubSubType || "",
          coachName: data.coachName || "",
          title: data.title || "",
          location: data.location || "",
          description: data.description || "",
          profileimg: data.profileimg || data.coachImage || null,
          coverimage: data.coverimage || null,
          website: data.website || "",
          facebook: data.facebook || "",
          instagram: data.instagram || "",
          tiktok: data.tiktok || "",
          achievements: Array.isArray(data.achievements) && data.achievements.length ? data.achievements[0] : "",
          credentials: Array.isArray(data.credentials) && data.credentials.length ? data.credentials : [""],
          amenities: Array.isArray(data.amenities) && data.amenities.length
            ? data.amenities
            : [{ title: "", description: "" }],
          headUserId: data.headUserId || null,
          capacity: data.capacity !== null && data.capacity !== undefined ? String(data.capacity) : "",
        });
        if (data.headUser) {
          setHeadUserOption(data.headUser);
        }
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, id, isEdit]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setHeadUserLoading(true);
      Axios.get(`${BACKEND_URL}coach-eligibility`, { params: { page: 1, limit: 50 } })
        .then((res) => {
          const docs = res?.data?.data?.docs || [];
          const options = docs
            .map((doc) => doc.userId)
            .filter(Boolean)
            .filter((user) => {
              const term = headUserSearchInput.trim().toLowerCase();
              if (!term) return true;
              return (
                (user.name || "").toLowerCase().includes(term) ||
                (user.mail || "").toLowerCase().includes(term)
              );
            });
          setHeadUserOptions(options);
        })
        .catch(showError)
        .finally(() => setHeadUserLoading(false));
    }, 400);
    return () => clearTimeout(handler);
  }, [BACKEND_URL, headUserSearchInput]);

  const updateCoach = (key, value) => {
    setCoach((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const loadPlaceSuggestions = (input) => {
    if (!input || input.trim().length < 2) {
      setPlaceOptions([]);
      return;
    }
    if (!autoServiceRef.current) return;

    autoServiceRef.current.getPlacePredictions({ input, types: ["geocode"] }, (predictions) => {
      setPlaceOptions((predictions || []).map((p) => ({ description: p.description, placeId: p.place_id })));
    });
  };

  const updateListField = (key, index, value) => {
    setCoach((prev) => ({
      ...prev,
      [key]: prev[key].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
    setErrors((prev) => ({ ...prev, [`${key}_${index}`]: "" }));
  };

  const addListField = (key) => {
    setCoach((prev) => ({
      ...prev,
      [key]: [...prev[key], ""],
    }));
  };

  const removeListField = (key, index) => {
    setCoach((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateAmenity = (index, field, value) => {
    setCoach((prev) => ({
      ...prev,
      amenities: prev.amenities.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addAmenity = () => {
    setCoach((prev) => ({
      ...prev,
      amenities: [...prev.amenities, { title: "", description: "" }],
    }));
  };

  const removeAmenity = (index) => {
    setCoach((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!coach.coachName.trim()) nextErrors.coachName = `${nameLabel} is required`;
    if (coach.type === "club" && !String(coach.clubSubType || "").trim()) {
      nextErrors.clubSubType = "Club Sub Type is required";
    }
    if (!coach.title.trim()) nextErrors.title = "Title is required";
    if (!coach.location.trim()) nextErrors.location = "Location is required";
    if (!coach.description.trim()) nextErrors.description = "Description is required";

    if (!coach.profileimg) {
      nextErrors.profileimg = "Cover / Profile image is required";
    } else if (coach.profileimg instanceof File) {
      if (!coach.profileimg.type.startsWith("image/")) nextErrors.profileimg = "Profile image must be a valid image file";
      else if (coach.profileimg.size > 2 * 1024 * 1024) nextErrors.profileimg = "Profile image exceeds 2MB limit";
    }

    if (!coach.coverimage) {
      nextErrors.coverimage = "Banner image is required";
    } else if (coach.coverimage instanceof File) {
      if (!coach.coverimage.type.startsWith("image/")) nextErrors.coverimage = "Cover / Banner image must be a valid image file";
      else if (coach.coverimage.size > 2 * 1024 * 1024) nextErrors.coverimage = "Cover / Banner image exceeds 2MB limit";
    }

    if (!coach.website.trim()) nextErrors.website = "Website is required";
    if (!coach.facebook.trim()) nextErrors.facebook = "Facebook is required";
    if (!coach.instagram.trim()) nextErrors.instagram = "Instagram is required";
    if (!coach.tiktok.trim()) nextErrors.tiktok = "TikTok is required";

    (coach.credentials || []).forEach((credential, index) => {
      if (!credential.trim()) nextErrors[`credentials_${index}`] = "Credential is required";
    });

    if (!String(coach.achievements || "").trim()) nextErrors.achievements = "Achievement is required";

    (coach.amenities || []).forEach((amenity, index) => {
      if (!String(amenity.title || "").trim()) nextErrors[`amenityTitle_${index}`] = "Amenity title is required";
      if (!String(amenity.description || "").trim()) nextErrors[`amenityDescription_${index}`] = "Amenity description is required";
    });

    if (!coach.headUserId) {
      nextErrors.headUserId = "Head user is required";
    }

    if (!String(coach.capacity || "").trim()) {
      nextErrors.capacity = "Capacity is required";
    } else {
      const parsedCapacity = Number(coach.capacity);
      if (!Number.isInteger(parsedCapacity) || parsedCapacity < 1) {
        nextErrors.capacity = "Capacity must be a positive integer";
      }
    }

    return nextErrors;
  };

  const submit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fill out the mandatory fields");
      return;
    }

    setLoading(true);
    const payload = new FormData();
    payload.append("coachName", coach.coachName.trim());
    payload.append("type", coach.type);
    payload.append("clubSubType", coach.type === "club" ? coach.clubSubType || "" : "");
    payload.append("title", coach.title.trim());
    payload.append("location", coach.location.trim());
    payload.append("description", coach.description.trim());
    payload.append("website", coach.website.trim());
    payload.append("facebook", coach.facebook.trim());
    payload.append("instagram", coach.instagram.trim());
    payload.append("tiktok", coach.tiktok.trim());
    payload.append("achievements", JSON.stringify([coach.achievements.trim()]));
    payload.append("credentials", JSON.stringify((coach.credentials || []).filter((item) => item.trim())));
    payload.append(
      "amenities",
      JSON.stringify(
        (coach.amenities || []).filter(
          (item) => item.title.trim() && item.description.trim()
        )
      )
    );
    payload.append("headUserId", coach.headUserId || "");
    payload.append("capacity", String(coach.capacity || "").trim());

    if (coach.profileimg instanceof File) payload.append("profileimg", coach.profileimg);
    if (coach.coverimage instanceof File) payload.append("coverimage", coach.coverimage);

    const request = isEdit
      ? Axios.put(`${BACKEND_URL}coach/${id}`, payload, { headers: { "Content-Type": "multipart/form-data" } })
      : Axios.post(`${BACKEND_URL}coach`, payload, { headers: { "Content-Type": "multipart/form-data" } });

    request
      .then((res) => {
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/coach-management");
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  return (
    <div className="container-fluid">
      <Helmet>
        <title>{isEdit ? `Edit ${coach.type}` : `Create Coach / Host / Club`}</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>{isEdit ? `Edit ${coach.type.toUpperCase()}` : "Create Coach / Host / Club"}</h2>
        <Button onClick={() => navigate("/coach-management")}>Back</Button>
      </div>

      <Typography variant="h6" sx={{ mb: 2 }}>General Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={coach.type}
              onChange={(e) => {
                const nextType = e.target.value;
                setCoach((prev) => ({
                  ...prev,
                  type: nextType,
                  clubSubType: nextType === "club" ? prev.clubSubType : "",
                }));
                setErrors((prev) => ({ ...prev, type: "", clubSubType: "" }));
              }}
            >
              <MenuItem value="coach">Coach</MenuItem>
              <MenuItem value="club">Club</MenuItem>
              <MenuItem value="host">Host</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {coach.type === "club" && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth required error={Boolean(errors.clubSubType)}>
              <InputLabel>Club Sub Type</InputLabel>
              <Select
                label="Club Sub Type"
                value={coach.clubSubType || ""}
                onChange={(e) => updateCoach("clubSubType", e.target.value)}
              >
                {Object.entries(CLUB_SUB_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
              {errors.clubSubType && <FormHelperText>{errors.clubSubType}</FormHelperText>}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label={nameLabel}
            value={coach.coachName}
            onChange={(e) => updateCoach("coachName", e.target.value)}
            error={Boolean(errors.coachName)}
            helperText={errors.coachName}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={headUserOptions}
            loading={headUserLoading}
            value={headUserOption}
            getOptionLabel={(option) => (option ? `${option.name || ""} (${option.mail || ""})` : "")}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onInputChange={(_, value) => setHeadUserSearchInput(value)}
            onChange={(_, selected) => {
              setHeadUserOption(selected);
              updateCoach("headUserId", selected?._id || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Head User (Eligible)"
                error={Boolean(errors.headUserId)}
                helperText={errors.headUserId}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="Title"
            placeholder="e.g. Head Coach / Event Host"
            value={coach.title}
            onChange={(e) => updateCoach("title", e.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            options={placeOptions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.description || "")}
            filterOptions={(x) => x}
            value={coach.location || null}
            onInputChange={(_, value) => {
              updateCoach("location", value);
              loadPlaceSuggestions(value);
            }}
            onChange={(_, selected) => {
              const description = typeof selected === "string" ? selected : selected?.description || "";
              updateCoach("location", description);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Location (Google Places)"
                error={Boolean(errors.location)}
                helperText={errors.location || (googleKey ? (placesReady ? "" : "Loading Google Places...") : "Missing REACT_APP_GOOGLE_MAPS_API_KEY")}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            type="number"
            label="Chat Capacity"
            inputProps={{ min: 1 }}
            value={coach.capacity}
            onChange={(e) => updateCoach("capacity", e.target.value)}
            error={Boolean(errors.capacity)}
            helperText={errors.capacity}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Description"
            value={coach.description}
            onChange={(e) => updateCoach("description", e.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
        </Grid>
      </Grid>

      {/* Images Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Images (Cover & Banner Image)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Cover / Profile Image</Typography>
              {profilePreview ? (
                <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd", width: "100%", maxWidth: 300 }}>
                  <img src={profilePreview} alt="Cover preview" style={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No cover / profile image selected.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                required
                inputProps={{ accept: "image/*" }}
                onChange={(e) => updateCoach("profileimg", e.target.files?.[0] || null)}
                error={Boolean(errors.profileimg)}
                helperText={errors.profileimg || (coach.profileimg instanceof File ? coach.profileimg.name : "Upload cover/profile image (max 2MB)")}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Banner Image</Typography>
              {coverPreview ? (
                <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd", width: "100%", maxWidth: 300 }}>
                  <img src={coverPreview} alt="Banner preview" style={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No banner image selected.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                required
                inputProps={{ accept: "image/*" }}
                onChange={(e) => updateCoach("coverimage", e.target.files?.[0] || null)}
                error={Boolean(errors.coverimage)}
                helperText={errors.coverimage || (coach.coverimage instanceof File ? coach.coverimage.name : "Upload banner image (max 2MB)")}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Social Links Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Links - Social</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Website"
            placeholder="https://example.com"
            value={coach.website}
            onChange={(e) => updateCoach("website", e.target.value)}
            error={Boolean(errors.website)}
            helperText={errors.website}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Facebook"
            placeholder="https://facebook.com/..."
            value={coach.facebook}
            onChange={(e) => updateCoach("facebook", e.target.value)}
            error={Boolean(errors.facebook)}
            helperText={errors.facebook}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Instagram"
            placeholder="https://instagram.com/..."
            value={coach.instagram}
            onChange={(e) => updateCoach("instagram", e.target.value)}
            error={Boolean(errors.instagram)}
            helperText={errors.instagram}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="TikTok"
            placeholder="https://tiktok.com/@..."
            value={coach.tiktok}
            onChange={(e) => updateCoach("tiktok", e.target.value)}
            error={Boolean(errors.tiktok)}
            helperText={errors.tiktok}
          />
        </Grid>
      </Grid>

      {/* Credentials Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Credentials</Typography>
      {(coach.credentials || []).map((credential, index) => (
        <Box key={`credential-${index}`} sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            required
            label={`Credential ${index + 1}`}
            placeholder="e.g. Certified Coach, Level 2 Instructor"
            value={credential}
            onChange={(e) => updateListField("credentials", index, e.target.value)}
            error={Boolean(errors[`credentials_${index}`])}
            helperText={errors[`credentials_${index}`]}
          />
          <IconButton onClick={() => removeListField("credentials", index)} disabled={coach.credentials.length === 1}>
            <DeleteOutline />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={() => addListField("credentials")}>
        Add Credential
      </Button>

      {/* Achievements Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Achievements</Typography>
      <TextField
        fullWidth
        required
        label="Achievement"
        placeholder="e.g. Winner of 2023 National Marathon"
        value={coach.achievements}
        onChange={(e) => updateCoach("achievements", e.target.value)}
        error={Boolean(errors.achievements)}
        helperText={errors.achievements}
      />

      {/* Amenities Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Amenities (Title & Description)</Typography>
      {(coach.amenities || []).map((amenity, index) => (
        <Card variant="outlined" key={`amenity-${index}`} sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                required
                label={`Amenity Title ${index + 1}`}
                placeholder="e.g. Shakeout Run"
                value={amenity.title || ""}
                onChange={(e) => updateAmenity(index, "title", e.target.value)}
                error={Boolean(errors[`amenityTitle_${index}`])}
                helperText={errors[`amenityTitle_${index}`]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label={`Amenity Description ${index + 1}`}
                placeholder="e.g. Pre-race morning 5k warm-up run with group"
                value={amenity.description || ""}
                onChange={(e) => updateAmenity(index, "description", e.target.value)}
                error={Boolean(errors[`amenityDescription_${index}`])}
                helperText={errors[`amenityDescription_${index}`]}
              />
            </Grid>
            <Grid item xs={12} md={1} sx={{ textAlign: "center" }}>
              <IconButton
                color="error"
                onClick={() => removeAmenity(index)}
                disabled={coach.amenities.length === 1}
              >
                <DeleteOutline />
              </IconButton>
            </Grid>
          </Grid>
        </Card>
      ))}
      <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={addAmenity}>
        Add Amenity
      </Button>

      <Box sx={{ mt: 4, mb: 4, display: "flex", gap: 2 }}>
        <Button variant="contained" disabled={loading} onClick={submit}>
          {loading ? "Saving..." : isEdit ? "Update" : "Submit"}
        </Button>
        <Button onClick={() => navigate("/coach-management")}>Cancel</Button>
      </Box>
    </div>
  );
};

export default CoachForm;
